"use client"

import Image from "next/image"
import { config } from "@/config"
import { Infinity, MapPin } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { socialLinks } from "@/components/profile/social-links"
import { ThemeToggle } from "@/components/theme-toggle"

export function DesktopLayout() {
  const handleClick = (url: string) => {
    if (typeof window !== "undefined") {
      window.open(url, "_blank", "noopener,noreferrer")
    }
  }

  return (
    <div className="from-background via-background to-muted/20 min-h-screen bg-linear-to-br bg-scroll px-3 py-6 sm:px-4 sm:py-8 md:px-6 md:py-10 lg:px-8">
      <div className="mx-auto max-w-6xl space-y-4 sm:space-y-6 lg:space-y-8">
        <Card className="relative overflow-hidden border-0 shadow-lg transition-shadow hover:shadow-2xl sm:shadow-xl">
          <div className="absolute top-4 right-4 z-30 sm:top-6 sm:right-6">
            <ThemeToggle />
          </div>
          <div className="from-primary/5 sm:from-primary/10 to-primary/5 sm:to-primary/10 pointer-events-none absolute inset-0 z-0 bg-linear-to-br via-transparent" />
          <CardContent className="relative z-10 p-6 sm:p-8 md:p-10 lg:p-12">
            <div className="flex flex-col items-center gap-6 sm:gap-8 md:flex-row md:items-start md:gap-10">
              <div className="relative shrink-0">
                <div className="from-primary via-primary to-secondary absolute -inset-2 animate-pulse rounded-full bg-linear-to-r opacity-40 blur-xl sm:-inset-3 sm:opacity-50" />
                <div className="relative">
                  <Image
                    src="/me.jpeg"
                    alt={config.name}
                    width={4096}
                    height={4096}
                    className="border-background ring-primary/20 size-40 rounded-2xl border-4 shadow-2xl ring-2 select-none sm:size-48 md:size-52 lg:size-56"
                    draggable={false}
                    onContextMenu={(e) => e.preventDefault()}
                  />
                  <div className="ring-background bg-primary absolute -right-1 -bottom-1 rounded-full p-1 shadow-lg ring-4 sm:-right-2 sm:-bottom-2">
                    <div className="bg-secondary/20 h-3 w-3 rounded-full sm:h-4 sm:w-4" />
                  </div>
                </div>
              </div>
              <div className="flex-1 space-y-3 text-center sm:space-y-4 md:text-left">
                <div className="space-y-2 sm:space-y-3">
                  <h1 className="text-2xl font-bold tracking-tight sm:text-3xl md:text-4xl lg:text-5xl">
                    {config.name}
                  </h1>
                  <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 md:justify-start">
                    <Badge
                      variant="default"
                      className="px-3 py-1 text-xs font-semibold shadow-md sm:px-4 sm:py-1.5 sm:text-sm"
                    >
                      {config.author.position}
                    </Badge>
                    <Badge
                      variant="outline"
                      className="gap-1 px-2.5 py-1 text-xs sm:px-3 sm:py-1.5 sm:text-sm"
                    >
                      <MapPin className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                      <span className="hidden sm:inline">
                        {config.author.location.full}
                      </span>
                      <span className="sm:hidden">
                        {config.author.location.short}
                      </span>
                    </Badge>
                  </div>
                </div>
                <p className="text-muted-foreground mx-auto max-w-2xl text-sm leading-relaxed sm:text-base md:mx-0 md:text-lg">
                  {config.description}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="relative overflow-hidden border-0 shadow-lg transition-shadow hover:shadow-2xl sm:shadow-xl">
          <div className="from-primary/5 to-primary/5 absolute inset-0 bg-linear-to-br via-transparent" />
          <CardHeader className="relative z-10 space-y-2 pb-4">
            <div className="flex items-center gap-3">
              <div className="via-border h-px flex-1 bg-linear-to-r from-transparent to-transparent" />
              <div className="text-center">
                <h2 className="from-primary to-primary/60 bg-linear-to-r bg-clip-text text-xl font-bold text-transparent sm:text-2xl">
                  Connect With Me
                </h2>
                <p className="text-muted-foreground mt-1 text-xs sm:text-sm">
                  Let&apos;s build something amazing together
                </p>
              </div>
              <div className="via-border h-px flex-1 bg-linear-to-r from-transparent to-transparent" />
            </div>
          </CardHeader>
          <CardContent className="relative z-10 pb-8">
            <div className="grid w-full grid-cols-3 flex-wrap justify-center gap-3 sm:flex sm:gap-4">
              {socialLinks.map((link, index) => (
                <button
                  key={index}
                  onClick={() => handleClick(link.url)}
                  className="group border-primary/20 sm:hover:border-primary sm:hover:bg-primary/10 relative flex h-20 w-full cursor-pointer flex-col items-center justify-center gap-1 rounded-2xl border bg-transparent px-3 transition-all duration-300 active:scale-95 sm:h-18 sm:w-20 sm:flex-row sm:gap-0 sm:px-0 sm:hover:scale-110"
                  aria-label={link.title}
                >
                  <div className="border-primary/20 from-primary/0 to-primary/5 absolute inset-0 rounded-2xl border bg-linear-to-br opacity-0 transition-opacity sm:group-hover:opacity-100" />
                  <div className="from-primary/10 absolute inset-0 rounded-2xl bg-linear-to-br via-transparent to-transparent opacity-0 blur-xl transition-opacity sm:group-hover:opacity-100" />
                  <span className="text-muted-foreground sm:group-hover:text-primary relative flex size-5 items-center justify-center transition-all duration-300 sm:size-7 sm:group-hover:scale-110">
                    {link.icon}
                  </span>
                  <span className="text-muted-foreground block max-w-full truncate px-1.5 pt-1 text-center text-xs font-medium sm:hidden">
                    {link.title}
                  </span>
                  <span className="bg-primary text-primary-foreground ring-border pointer-events-none absolute -top-11 left-1/2 hidden -translate-x-1/2 rounded-md px-2.5 py-1.5 text-xs font-medium whitespace-nowrap opacity-0 shadow-xl ring-1 transition-opacity sm:-top-12 sm:inline-flex sm:px-3 sm:py-2 sm:group-hover:opacity-100">
                    {link.title}
                    <span className="bg-primary absolute -bottom-1 left-1/2 h-2 w-2 -translate-x-1/2 rotate-45" />
                  </span>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-4">
          <Card className="relative overflow-hidden border-0 shadow-md transition-all hover:shadow-xl sm:shadow-lg">
            <div className="from-primary/5 to-primary/5 absolute inset-0 bg-linear-to-br via-transparent" />
            <CardContent className="relative z-10 flex flex-col items-center justify-center p-5 sm:p-6">
              <div className="text-primary mb-1.5 text-2xl font-bold sm:mb-2 sm:text-3xl">
                {socialLinks.length - 1}+
              </div>
              <div className="text-muted-foreground text-center text-xs sm:text-sm">
                Social Platforms
              </div>
            </CardContent>
          </Card>
          <Card className="relative overflow-hidden border-0 shadow-md transition-all hover:shadow-xl sm:shadow-lg">
            <div className="from-primary/5 to-primary/5 absolute inset-0 bg-linear-to-br via-transparent" />
            <CardContent className="relative z-10 flex flex-col items-center justify-center p-5 sm:p-6">
              <div className="text-primary mb-1.5 text-2xl font-bold sm:mb-2 sm:text-3xl">
                <Infinity className="size-8 sm:size-10" />
              </div>
              <div className="text-muted-foreground text-center text-xs sm:text-sm">
                Lines of Code
              </div>
            </CardContent>
          </Card>
          <Card className="relative overflow-hidden border-0 shadow-md transition-all hover:shadow-xl sm:shadow-lg">
            <div className="from-primary/5 to-primary/5 absolute inset-0 bg-linear-to-br via-transparent" />
            <CardContent className="relative z-10 flex flex-col items-center justify-center p-5 sm:p-6">
              <div className="text-primary mb-1.5 text-2xl font-bold sm:mb-2 sm:text-3xl">
                24/7
              </div>
              <div className="text-muted-foreground text-center text-xs sm:text-sm">
                Learning & Building
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
